import { AgGridTable } from 'components/AgGrid/AgGridTable';
import type {
    BimActKc2TotalCalcMethodEnum,
    BimActKc2TotalItemData,
    BimCalculateInflationEnum,
    BimLimitedCostTypeEnum,
    ContractStatusEnum,
    Maybe,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { Loader } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ActKc2DetailsToolbox } from './components/ActKc2DetailsToolbox';
import { useBimActsKc2TotalDetailsColumns, useUpdateActTotalLimited } from './hooks';
import { useBimActKC2InflationValue } from './hooks/useBimActKC2InflationValue';

import Styles from './BimWorkOrderActsKc2.module.css';

interface IBimWorkOrderActTotalDetails {
    actKC2Id: number;
    actKC2Details: Maybe<BimActKc2TotalItemData[] | undefined>;
    onSuccess: () => void;
    isLoading: boolean;
    defaultColDef?: ColDef;
    styleClass?: string;
    contractStatus?: ContractStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    statusProject?: ProjectStatusEnum;
    isActionDisabled?: boolean;
    isReadOnly?: boolean;
    inflationCalcType?: BimCalculateInflationEnum;
    calcMethod?: BimActKc2TotalCalcMethodEnum;
    hasDeflation?: boolean;
}

const getRowId = ({ data }: { data: BimActKc2TotalItemData }) =>
    data?.isInflation ? `${data?.id}-${data?.workOrderSeasonId}` : data?.id;

export const BimWorkOrderActKc2TotalDetails = (props: IBimWorkOrderActTotalDetails) => {
    const {
        actKC2Id,
        styleClass,
        defaultColDef,
        onSuccess,
        actKC2Details,
        contractStatus,
        workOrderStatus,
        statusProject,
        isActionDisabled,
        isReadOnly,
        inflationCalcType,
        hasDeflation,
        calcMethod,
    } = props;

    const { versionId } = useParams();

    const isVersion = !!versionId;

    const {
        updateLimitedHandler,
        resetCalculatedHandler,
        setInputValueHandler,
        setTotalInflationHandler,
        isCommitting,
    } = useUpdateActTotalLimited({
        onSuccess,
    });

    const { bimUseActKC2InflationValueMakeRequest, setInflationInProgress } = useBimActKC2InflationValue({
        onSuccess,
    });

    const { columns } = useBimActsKc2TotalDetailsColumns({
        actKC2Id,
        contractStatus,
        workOrderStatus,
        updateLimitedHandler,
        resetCalculatedHandler,
        setInputValueHandler,
        setTotalInflationHandler,
        statusProject,
        isVersion,
        isActionDisabled,
        isReadOnly,
        inflationCalcType,
        isCommitting,
    });

    const allAvailableLimitedTypes = useMemo(
        () =>
            actKC2Details?.reduce((acc: BimLimitedCostTypeEnum[], item) => {
                if (item.limitedCostType) {
                    acc.push(item.limitedCostType);
                }

                return acc;
            }, []),
        [actKC2Details],
    );

    return (
        <div className={`ag-theme-alpine ${styleClass}`}>
            {!isReadOnly && (
                <ActKc2DetailsToolbox
                    actKC2Id={actKC2Id}
                    updateLimitedHandler={updateLimitedHandler}
                    resetCalculatedHandler={resetCalculatedHandler}
                    limitedTypes={allAvailableLimitedTypes}
                    contractStatus={contractStatus}
                    workOrderStatus={workOrderStatus}
                    setTotalInflationHandler={setTotalInflationHandler}
                    statusProject={statusProject}
                    isVersion={isVersion}
                    isActionDisabled={isActionDisabled}
                    inflationCalcType={inflationCalcType}
                    calcMethod={calcMethod}
                    onUseActKC2InflationValue={bimUseActKC2InflationValueMakeRequest}
                    hasDeflation={hasDeflation}
                />
            )}

            <AgGridTable
                getRowId={getRowId}
                rowData={actKC2Details}
                columnDefs={columns}
                defaultColDef={defaultColDef}
                containerStyle={{ paddingBottom: 20, height: isReadOnly ? '100%' : '97%' }}
                overlayNoRowsTemplate="Нет данных"
            />

            {(isCommitting || setInflationInProgress) && (
                <div className={Styles.overlayLoader}>
                    <Loader />
                </div>
            )}
        </div>
    );
};

BimWorkOrderActKc2TotalDetails.displayName = 'BimWorkOrderActTotalDetails';
